<template>
  <b-card no-body>
    <b-card-body id="customers-card-body">
      <div class="d-flex justify-content-end w-full">
        <b-button variant="primary" type="button" size="sm" class="mb-2" @click="createRegion()">
          <feather-icon icon="PlusIcon" size="15" />
          <template v-if="providerDetails.region_model_type === 1"
            >{{ t('Add New Region') }}
          </template>
          <template v-if="providerDetails.region_model_type === 2"
            >{{ t('Add New Country') }}
          </template>
          <b-tooltip
            triggers="hover"
            target="default-filter-new-btn"
            :title="t('Create Customer User')"
            :delay="{ show: 0, hide: 50 }"
          />
        </b-button>
      </div>
      <div class="d-flex align-items-center mb-3">
        <label class="mb-0 mr-2 font-weight-bolder">{{ t('Current Model') }}:</label>
        <b-form-input
          id="region_model_type"
          :value="getProviderModelTypeText(providerDetails.region_model_type)"
          class="form-control-merge w-25"
          name="region_model_type"
          readonly
        />
        <b-button
          class="ml-1"
          variant="primary"
          :disabled="changeRegionSubmitting"
          @click="changeModelType(providerDetails.region_model_type)"
        >
          <template v-if="changeRegionSubmitting">
            {{ t('Loading') }}
          </template>
          <template v-else>
            <template v-if="providerDetails.region_model_type === 1">
              {{ t('Change to Country') }}
            </template>
            <template v-else-if="providerDetails.region_model_type === 2">
              {{ t('Change to Region') }}
            </template>
          </template>
        </b-button>
      </div>
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-if="props.column.field === 'countries.name'">
            {{ (props.row.countries || []).map(c => $t(c.name)).join(', ') || '-' }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailRegion(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="`${t('Edit')}`"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-region-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteProviderRegion(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-region-button-${props.row.id}`"
                  :title="
                    providerDetails.region_model_type === 1
                      ? t('Delete Provider Region')
                      : t('Delete Country')
                  "
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BTooltip, BButton, BFormInput } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import store from '@/store/index';
import { mapGetters } from 'vuex';
import TableNoResultFound from '@/components/TableNoResultFound.vue';

export default {
  components: {
    TableNoResultFound,
    BFormInput,
    VueGoodTable,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: ''
      },
      regionModelTypes: [],
      providerId: null,
      showAllCountries: {},
      changeRegionSubmitting: false
    };
  },

  computed: {
    ...mapGetters({
      regionModelType: 'app/regionModelType',
      providerDetails: 'app/providerDetails'
    }),
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },

    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      const columns = [];

      if (this.providerDetails.region_model_type === 1) {
        columns.push({
          label: this.$t('Name'),
          field: 'name'
        });
      }

      columns.push(
        {
          label: this.$t('Country'),
          field: 'countries.name'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      );

      return columns;
    }
  },

  async mounted() {
    this.providerId = this.$route.params.id;

    if (this.regionModelType.length === 0) {
      await this.$store.dispatch('app/getRegionModelType');
    }

    if (
      Object.keys(this.providerDetails).length < 1 ||
      (Object.keys(this.providerDetails).length > 0 &&
        parseInt(this.providerDetails.id) !== parseInt(this.providerId))
    ) {
      await this.$store.dispatch('app/getProviderDetails', this.providerId);
    }

    await this.loadItems();
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      const routerId = this.$route.params.id;

      // const requestData = {};

      this.isLoading = true;

      useService
        .getAllRegions(routerId)
        .then(response => {
          this.rows = response.data;
        })
        .finally(() => {
          this.isLoading = false;
        });

      // if(requestData.dateFrom && requestData.dateTo && (new Date(requestData.dateFrom) > new Date(requestData.dateTo))) {
      //   this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'))
      //   return false
      // }
    },

    setFilter(data) {
      if (data) {
        this.filterValues = data;
        this.filter.page = 1;
        this.loadItems();
      }
    },

    deleteProviderRegion(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Region Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteProviderRegionRequest(id)
            .then(response => {
              this.showSuccessMessage(`${response.message || this.$t('Region Was Deleted')}`);

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    async deleteProviderRegionRequest(id) {
      return useService.deleteProviderRegion(id);
    },

    detailRegion(region_id) {
      const { id } = this.$route.params;
      this.$router.push({
        name: 'provider_region_detail',
        params: {
          id,
          region_id
        }
      });
    },

    createRegion() {
      const { id } = this.$route.params;
      this.$router.push(`/providers/${id}/regions/create`);
    },

    getAllPricing(id) {
      this.$router.push({
        name: 'regions_pricing_list',
        params: { id: this.$route.params.id, region_id: id }
      });
    },

    getProviderModelTypeText(type) {
      const model = this.regionModelType.find(item => parseInt(item.value) === parseInt(type));
      return model ? model.text : '-';
    },

    changeModelType(type) {
      const warningText =
        type === 1
          ? this.$t('Region Model Delete Warning')
          : this.$t('Country Model Delete Warning Reverse');

      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: warningText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(result => {
        if (result.isConfirmed) {
          const newModelType = type === 1 ? 2 : 1;
          const requestData = {
            region_model_type: newModelType
          };

          this.changeRegionSubmitting = true;
          useService
            .setProviderRegionModelType(this.providerId, requestData)
            .then(response => {
              if (response.status) {
                this.providerDetails.region_model_type = newModelType;
                this.$store.commit('SET_PROVIDER_DETAILS', {
                  ...this.providerDetails,
                  region_model_type: newModelType
                });
                this.loadItems();
              }
            })
            .finally(() => {
              this.changeRegionSubmitting = false;
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
